import React, { useState, useEffect, useContext } from 'react';
import { Box, Tabs, Tab, Button, IconButton } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ArticleIcon from '@mui/icons-material/Article';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LogoutIcon from '@mui/icons-material/Logout';
import RepresentativeData from './RepresentativeData';
import AgendaEditTab from './AgendaEditTab';
import PostsEditTab from './PostsEditTab';
import { decrypt, encrypt } from '../../../Security';
import { AuthContext } from '../../Auth/AuthContext';
import { useNavigate } from 'react-router-dom';

const VV = {
  primary: '#3F2B96',
  secondary: '#7F5AF0',
  accent: '#00C896',
  bg: '#F7F8FC',
  border: 'rgba(63,43,150,0.10)',
};

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index} style={{ minHeight: '100vh' }}>
      {value === index && children}
    </div>
  );
}

const EditTabs = ({ repId, setRepId, isAdmin }) => {
  const [value, setValue] = useState(0);
  const [agendas, setAgendas] = useState([]);
  const { isAuthenticated, adminId } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleBack = () => {
    console.log('🔙 Back button clicked');
    if (setRepId) {
      console.log('🔄 Clearing repId and returning to search');
      setRepId('');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(process.env.REACT_APP_PUBLIC_SERVER_URI + '/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
      localStorage.clear();
      navigate('/admin');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const getAgenda = async () => {
    try {
      const id = repId || window.location.href.split('/').pop();
      const response = await fetch(
        process.env.REACT_APP_PUBLIC_SERVER_URI + '/api/v1/reps/agendas/' + id,
        { method: 'GET', headers: { 'Content-Type': 'application/json' }, credentials: 'include' }
      );
      const data = decrypt(await response.json());
      setAgendas(data.agendas || []);
    } catch (e) { console.log('Error:', e); }
  };

  const updateAgendasDB = async (newAgendas = null) => {
    try {
      const id = repId || window.location.href.split('/').pop();
      const url = process.env.REACT_APP_PUBLIC_SERVER_URI + '/adminWeb/repAgenda/update';
      const response = await fetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: encrypt({ agendas: newAgendas || agendas, repId: id }),
      });
      const data = decrypt(await response.json());
      if (data.msg === 'SUCCESS') { getAgenda(); }
    } catch (err) { console.error(err); }
  };

  const removeAgenda = async (myAgd) => {
    const agds = agendas.filter(
      agd => !(agd.topic === myAgd.topic && agd.detail === myAgd.detail)
    );
    await updateAgendasDB(agds);
    getAgenda();
  };

  const resetData = () => { setAgendas([]); setValue(0); };

  useEffect(() => { getAgenda(); }, []);

  const TABS = [
    { label: 'Profile', icon: <PersonIcon fontSize="small" /> },
    { label: 'Agenda', icon: <EventNoteIcon fontSize="small" /> },
    { label: 'Posts', icon: <ArticleIcon fontSize="small" /> },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: VV.bg }}>
      <Box sx={{
        background: `linear-gradient(135deg, ${VV.primary} 0%, ${VV.secondary} 100%)`,
        boxShadow: '0 4px 24px rgba(63,43,150,0.25)',
        position: 'sticky', 
        top: { xs: 56, sm: 60, md: 64 }, 
        zIndex: 999,
        width: '100%',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1 }}>
          {isAdmin ? (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{
                color: 'rgba(255,255,255,0.85)',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              Back to Search
            </Button>
          ) : (
            <Button
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{
                color: 'rgba(255,255,255,0.85)',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              Logout
            </Button>
          )}
        </Box>
        <Tabs
          value={value}
          onChange={(_, v) => setValue(v)}
          variant="fullWidth"
          TabIndicatorProps={{
            style: { backgroundColor: VV.accent, height: 3, borderRadius: '3px 3px 0 0' },
          }}
          sx={{
            '& .MuiTab-root': {
              color: 'rgba(255,255,255,0.65)', fontWeight: 600, fontSize: '0.85rem',
              textTransform: 'none', py: 1.8, transition: 'color 0.25s',
            },
            '& .Mui-selected': { color: '#fff !important' },
          }}
        >
          {TABS.map((tab, i) => (
            <Tab key={i} icon={tab.icon} label={tab.label} iconPosition="start" />
          ))}
        </Tabs>
      </Box>

      <TabPanel value={value} index={0}>
        <RepresentativeData
          resetData={resetData}
          setRepId={setRepId}
          repId={repId}
          agendas={agendas}
          setAgendas={setAgendas}
          removeAgenda={removeAgenda}
        />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <AgendaEditTab
          repId={repId}
          agendas={agendas}
          setAgendas={setAgendas}
          removeAgenda={removeAgenda}
          updateAgendasDB={updateAgendasDB}
          selectedSection="Agenda"
        />
      </TabPanel>
      <TabPanel value={value} index={2}>
        <PostsEditTab repId={repId} selectedSection="Posts" />
      </TabPanel>
    </Box>
  );
};

export default EditTabs;
